// Single source of truth for the store-wide currency symbol on the client.
// Mirrored on the server at server/utils/currency.js - keep in sync.
export const CURRENCY_SYMBOLS = {
  THB: '฿',
  INR: '₹',
};

// Falls back to THB for any unrecognized/legacy value (e.g. a free-text
// value left over from the old per-document currency picker).
export const getCurrencySymbol = (code) => CURRENCY_SYMBOLS[code] || CURRENCY_SYMBOLS.THB;

// The store's tax terminology follows its currency: Thai stores call it VAT,
// Indian stores call the same line item Tax. Falls back to VAT (THB) for any
// unrecognized/legacy value.
export const TAX_LABELS = {
  THB: 'VAT',
  INR: 'Tax',
};

export const getTaxLabel = (code) => TAX_LABELS[code] || TAX_LABELS.THB;

// Cash-denomination rounding for the POS payable total ONLY - e.g. Thai
// coins come in quarter-baht steps, so the amount a customer actually pays
// and gets change for has to land on a real coin value. `roundingRules` is
// the { [currencyCode]: increment } map from GlobalSettings (SuperAdmin's
// Currency Rounding card) - 0/missing means keep standard 2-decimal
// precision, unchanged from today.
//
// Deliberately NOT used for Purchase or Stock Adjustment totals: those have
// to match a supplier's exact invoice amount / exact inventory valuation,
// so rounding them would introduce a permanent reconciliation gap instead
// of solving a real cash-handling problem.
//
// Call this exactly once, on the final total, after tax/discount/service
// charge are already folded in - never on a subtotal or a line item.
export function applyCurrencyRounding(amount, currencyCode, roundingRules) {
  const raw = Number(amount) || 0;
  const increment = Number(roundingRules?.[currencyCode]) || 0;
  if (!increment) {
    return { rounded: parseFloat(raw.toFixed(2)), roundOff: 0 };
  }
  const rounded = parseFloat((Math.round(raw / increment) * increment).toFixed(2));
  const roundOff = parseFloat((rounded - raw).toFixed(2));
  return { rounded, roundOff };
}

// Drops a redundant ".00" once the store's rounding rule (e.g. rounding to
// the nearest whole baht/rupee) has already produced a whole number - a
// total that still carries cents (23.50) keeps them, only a clean whole
// amount (24) prints without them. Only ever call this on the final,
// already-rounded payable total - not on sub-totals or line items, which
// should always show their full 2-decimal precision.
export function formatFinalAmount(value) {
  const num = Number(value) || 0;
  return Number.isInteger(num) ? String(num) : num.toFixed(2);
}

// Thousand-separated, always 2 decimals - e.g. 581989.94 -> "581,989.94".
// Same regex-based approach already used elsewhere in the app (Purchases.js's
// toCommas etc.) and what server/documents/_theme.js's money() already does
// for PDFs, so this brings the on-screen report tables in line with what the
// PDF/server side already renders instead of the raw un-grouped toFixed(2).
export function formatMoney(value) {
  const num = Number(value);
  const safe = Number.isFinite(num) ? num : 0;
  const sign = safe < 0 ? "-" : "";
  const [whole, fraction] = Math.abs(safe).toFixed(2).split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${sign}${grouped}.${fraction}`;
}
